import { ChangeDetectorRef, Component, EventEmitter, Input, Output, signal, ViewChild } from '@angular/core';
import { assistantBulletsContent, assistantContent } from '../../helpers/assistant.content';
import { Experiences } from '@local/client-contracts';
import { MultiSelectModel, MultiSelectSelectedEvent } from '@local/ui-infra';
import { ExperiencesService } from 'src/app/bar/services/experiences.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual, cloneDeep } from 'lodash';
import { map, distinctUntilChanged } from 'rxjs';
import { AssistantBulletPanelAction, AssistantBulletType } from '../../models/assistant-bullet.model';
import { clampMax } from '@local/ts-infra';
import { AssistantsConstants } from '@local/common-web';
import { CustomizationInputsModel } from '../slack-customization-inputs-list/slack-customization-inputs-list.component';
import { AssistantPopupService } from '../../services/assistant-popup.service';
import { actionButtonsContent } from '../../helpers/assistant-action-buttons.content';
import { SlackActionButtonsPopupModel } from '../slack-action-buttons-popup/slack-action-buttons-popup.component';

type AssistantBullets = Partial<{
  [key in AssistantBulletType]: boolean;
}>;

type SlackPreferences = {
  endReaction: boolean;
  startReaction: boolean;
  feedbackReasoning: boolean;
  answerValidation: Experiences.AnswerValidation;
  enableRelatedQuestions: boolean;
  relatedQuestionLimit: number;
  customSuggestion: Experiences.ExperienceCustomSuggestion;
  knowledgeResourcesLimit: number;
};

@UntilDestroy()
@Component({
  selector: 'slack-preferences',
  templateUrl: './slack-preferences.component.html',
  styleUrls: ['../../base-preferences.scss', './slack-preferences.component.scss'],
})
export class SlackPreferencesComponent {
  readonly LIMIT_RELATED_QUESTION_MAX = AssistantsConstants.RELATE_QUESTION_DEFAULT_LIMIT;
  readonly LIMIT_RELATED_QUESTION_MIN = 1;
  readonly LIMIT_RELATED_ARTICLES_MAX = AssistantsConstants.RELATE_ARTICLES_DEFAULT_LIMIT;
  readonly LIMIT_RELATED_ARTICLES_MIN = 1;
  readonly assistantContent = assistantContent;
  readonly bulletsContent = assistantBulletsContent.slack;
  private assistantId: string;
  viewOnly: boolean;
  slackProperties: SlackPreferences;
  assistantBullets: AssistantBullets = {
    enableRelatedQuestions: true,
    enableRelatedArticles: true,
    startReaction: true,
    endReaction: true,
    feedbackReasoning: true,
    enableExpertsValidation: false,
    actionButtonCustomization: false,
    replyPendingMessage: true,
    showRejectReaction: true,
    showApprovalReaction: true,
  };
  relateQuestionLimit: number;
  knowledgeResourcesLimit: number;
  @Input() set assistant(val: Experiences.ExperienceItem<'slack'>) {
    this.assistantId = val.id;
    this.viewOnly = val.permissionRole === 'viewer';
    this.channelMembers = cloneDeep(val.channelMembers) || [];
    this.initSlackProperties(val.settings);
    this.initBullets();
    this.initCustomSuggestions();
  }
  @Output() updateProperties = new EventEmitter<SlackPreferences>();

  //experts multi select
  private channelMembers: Experiences.ExperienceChannelMember[] = [];
  private expertsMultiSelectIsDirty: boolean;
  validationExpertsMultiselect: MultiSelectModel;
  selectedExperts: Experiences.ExperienceChannelMember[] = [];

  //custom suggestion actions
  readonly TITLE_CHARS_LIMIT = 50;
  readonly DESCRIPTION_CHARS_LIMIT = 100;
  slackCustomSuggestionActions = signal<CustomizationInputsModel[]>([]);
  isValidCustomSuggestionActions = signal<boolean>(false);
  slackCustomSuggestionsInputs = signal<AssistantBulletPanelAction[]>([]);
  inputsErrors = signal<string[]>(['', '']);

  constructor(
    private cdr: ChangeDetectorRef,
    private assistantService: ExperiencesService,
    private assistantPopupService: AssistantPopupService
  ) {}

  private initSlackProperties({
    endReaction,
    startReaction,
    feedbackReasoning,
    answerValidation,
    enableRelatedQuestions,
    relatedQuestionLimit,
    customSuggestion,
    knowledgeResourcesLimit,
  }: Experiences.ExperienceSlackSettings) {
    this.slackProperties = {
      endReaction,
      startReaction,
      feedbackReasoning,
      answerValidation,
      enableRelatedQuestions,
      relatedQuestionLimit,
      customSuggestion,
      knowledgeResourcesLimit,
    };
  }

  private initBullets() {
    const answerValidation = this.slackProperties?.answerValidation;
    const enableExpertsValidation: boolean = answerValidation?.enabled && !!answerValidation?.experts?.length;
    this.assistantBullets = {
      enableRelatedQuestions: this.slackProperties?.enableRelatedQuestions,
      enableRelatedArticles: !!this.slackProperties?.knowledgeResourcesLimit,
      startReaction: this.slackProperties?.startReaction,
      endReaction: this.slackProperties?.endReaction,
      feedbackReasoning: this.slackProperties?.feedbackReasoning,
      enableExpertsValidation,
      replyPendingMessage: answerValidation?.replyPendingMessage,
      showRejectReaction: answerValidation?.showRejectReaction,
      showApprovalReaction: answerValidation?.showApprovalReaction,
      actionButtonCustomization: !!this.slackProperties.customSuggestion?.actions?.length,
    };
    this.initRelatedQuestion();
    this.initRelatedArticles();
    this.expertsMultiSelectIsDirty = false;
    this.createExpertsMultiselect();
    this.initChannelMembers();
  }

  private initCustomSuggestions() {
    const customSuggestions = cloneDeep(this.slackProperties.customSuggestion);

    const panelActionsInputs = this.bulletsContent.find((b) => b.type === 'actionButtonCustomization')?.panelActions.inputs;
    this.slackCustomSuggestionsInputs.set([...panelActionsInputs]);
    this.slackCustomSuggestionsInputs()[0].content = customSuggestions?.title;
    this.slackCustomSuggestionsInputs()[1].content = customSuggestions?.messageSuccess;
    if (this.viewOnly) {
      if (!customSuggestions?.title) {
        this.slackCustomSuggestionsInputs()[0] = null;
      }
      if (!customSuggestions?.messageSuccess) {
        this.slackCustomSuggestionsInputs()[1] = null;
      }
      this.slackCustomSuggestionsInputs.set(this.slackCustomSuggestionsInputs().filter((input) => input));
    }

    this.isValidCustomSuggestionActions.set(false);
    this.slackCustomSuggestionActions.set([]);
    if (customSuggestions?.actions?.length) {
      this.isValidCustomSuggestionActions.set(true);
      this.slackCustomSuggestionActions.set(
        customSuggestions.actions.map((action) => {
          return {
            content: [
              { id: 'text', placeholder: 'Button name', input: action.text },
              { id: 'url', placeholder: 'Paste the button URL', input: action.url },
            ],
            mode: 'constantRow',
          };
        })
      );
    }
  }

  private setLimitMessages(value: string, index: number) {
    const limit: number = index ? this.DESCRIPTION_CHARS_LIMIT : this.TITLE_CHARS_LIMIT;
    let errorMessage: string = '';
    if (value?.length >= limit) {
      errorMessage = 'Character limit reached';
    }
    this.inputsErrors.update((errors) => {
      const updatedErrors = [...errors];
      updatedErrors[index] = errorMessage;
      return updatedErrors;
    });
  }

  onCustomSuggestionsInputsBlur() {
    this.inputsErrors.set(['', '']);
  }

  onCustomSuggestionsInputsFocus($event, index: number) {
    if (this.viewOnly) {
      return;
    }
    this.setLimitMessages($event.value, index);
  }

  onCustomSuggestionsInputsChanged($event, index: number) {
    this.setLimitMessages($event, index);
    this.slackCustomSuggestionsInputs()[index].content = $event;
    this.updateAssistantProperties();
  }

  onCustomSuggestionsActionsChanged($event) {
    this.slackCustomSuggestionActions.set(cloneDeep($event));
    this.isValidCustomSuggestionActions.set(!!$event);
    this.updateAssistantProperties();
  }

  private initRelatedQuestion() {
    this.relateQuestionLimit = this.slackProperties.relatedQuestionLimit;
  }

  private initRelatedArticles() {
    this.knowledgeResourcesLimit = this.slackProperties.knowledgeResourcesLimit || this.LIMIT_RELATED_ARTICLES_MAX;
  }

  private initChannelMembers() {
    this.assistantService.visible$
      .pipe(
        untilDestroyed(this),
        map((all) => all?.find((a) => a.id === this.assistantId)),
        distinctUntilChanged()
      )
      .subscribe((a) => {
        const channelMembersChanged: boolean = a?.channelMembers && !isEqual(a.channelMembers, this.channelMembers);
        if (channelMembersChanged) {
          this.channelMembers = cloneDeep(a.channelMembers);
          this.createExpertsMultiselect();
        }
      });
  }

  private createExpertsMultiselect() {
    const members: Experiences.ExperienceChannelMember[] = this.channelMembers || [];
    this.validationExpertsMultiselect = {
      options: members,
      optionLabel: 'name',
    };
    let selectedExpertsIds: string[] = [];
    if (this.expertsMultiSelectIsDirty) {
      selectedExpertsIds = (this.selectedExperts || []).map((s) => s.id);
    } else {
      selectedExpertsIds = (this.slackProperties?.answerValidation?.experts || []).map((e) => e.id);
    }
    setTimeout(() => {
      this.selectedExperts = members.filter((m) => selectedExpertsIds.includes(m.id));
      this.cdr.markForCheck();
    }, 0);
  }

  onUpdateSync($event, id: string) {
    this.assistantBullets[id] = $event.checked;
    this.updateAssistantProperties();
  }
  updateAssistantProperties() {
    this.slackProperties = {
      ...this.slackProperties,
      endReaction: this.assistantBullets.endReaction,
      startReaction: this.assistantBullets.startReaction,
      feedbackReasoning: this.assistantBullets.feedbackReasoning,
      answerValidation: {
        enabled: !!this.assistantBullets.enableExpertsValidation && !!this.selectedExperts?.length,
        experts: (this.selectedExperts || []).map((s) => ({ id: s.id })),
        replyPendingMessage: this.assistantBullets.replyPendingMessage,
        showRejectReaction: this.assistantBullets.showRejectReaction,
        showApprovalReaction: this.assistantBullets.showApprovalReaction,
      },
      enableRelatedQuestions: this.assistantBullets.enableRelatedQuestions,
      relatedQuestionLimit: this.relateQuestionLimit,
      customSuggestion:
        !!this.assistantBullets.actionButtonCustomization && this.isValidCustomSuggestionActions()
          ? {
              title: this.slackCustomSuggestionsInputs()[0].content || '',
              messageSuccess: this.slackCustomSuggestionsInputs()[1].content || '',
              actions: this.slackCustomSuggestionActions()?.map((s) => {
                return { text: s.content[0].input, url: s.content[1].input };
              }),
            }
          : { title: '', messageSuccess: '', actions: [] },
      knowledgeResourcesLimit: !!this.assistantBullets.enableRelatedArticles ? this.knowledgeResourcesLimit : 0,
    };
    this.updateProperties.emit(this.slackProperties);
  }

  onSelectExpertsChanged(event: MultiSelectSelectedEvent) {
    this.selectedExperts = event.value;
    this.expertsMultiSelectIsDirty = true;
    this.updateAssistantProperties();
  }

  changeRelatedQuestionsLimit($event: string) {
    const limitCount = Number($event);
    if (isNaN(limitCount)) {
      return;
    }
    this.relateQuestionLimit = clampMax(this.LIMIT_RELATED_QUESTION_MIN, limitCount, this.LIMIT_RELATED_QUESTION_MAX);
    this.updateAssistantProperties();
  }

  onClickInfoIcon(bulletType: AssistantBulletType) {
    if (bulletType === 'actionButtonCustomization') {
      this.openPreviewPopup(actionButtonsContent);
    }
  }

  openPreviewPopup(model?: SlackActionButtonsPopupModel) {
    if (!model) {
      model = this.buildSlackActionButtonsPopupModel();
    }
    this.assistantPopupService.openSlackActionButtonsPopup(model);
  }

  private buildSlackActionButtonsPopupModel(): SlackActionButtonsPopupModel {
    const customSuggestion = this.slackProperties.customSuggestion;
    return {
      popupTitle: 'Preview customizations',
      title: customSuggestion.title,
      description: customSuggestion.messageSuccess,
      actionButtons: customSuggestion.actions.map((a) => ({ text: a.text })),
    };
  }

  changeRelatedArticlesLimit($event: string) {
    const limitCount = Number($event);
    if (isNaN(limitCount)) {
      return;
    }
    this.knowledgeResourcesLimit = clampMax(this.LIMIT_RELATED_ARTICLES_MIN, limitCount, this.LIMIT_RELATED_ARTICLES_MAX);
    this.updateAssistantProperties();
  }
}
